import type { EmployeeResponse } from '@palaccount/contracts';
import * as React from 'react';

import type { TypeaheadOption } from '@/components/ui/typeahead';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';

import { useEmployeeSearch, useEmployeesHeld, type EmployeeListFilters } from './api';

/**
 * THE EMPLOYEE PICKER (GEN-50) — HR-04's leave form and HR-05's attendance
 * screen, and PAGINATION.md §5a's last entry.
 *
 * `heldId` is the person the form is holding. It is fetched separately from
 * the search, and on this route that read is the one that matters most:
 * `includeTerminated` is off by default, so without it every leave request and
 * every attendance correction belonging to a LEAVER would render with an empty
 * name — on the very records that exist to say what that person did.
 */
export function useEmployeePicker(
  heldId: string | null | undefined,
  filters: EmployeeListFilters = {},
  /**
   * HR-01 stores an Arabic name beside the Latin one and every screen prints
   * whichever the reader is in, so the ROW is the caller's — `useAr()` is a
   * hook and this file is not a component. The SEARCH is unaffected: it
   * matches both names and the code either way, so a reader in Arabic can
   * still find somebody by the Latin spelling on their passport.
   */
  toOption: (employee: EmployeeResponse) => TypeaheadOption = employeeOption,
): PickerState<EmployeeResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const ids = React.useMemo(() => (heldId ? [heldId] : []), [heldId]);

  const matches = useEmployeeSearch(search, filters);
  const held = useEmployeesHeld(ids);

  return usePickerState<EmployeeResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: held.data,
    toOption,
    loading: matches.isFetching,
  });
}

/**
 * HR-01 §6's own two columns: the person, and the code beside them. A leaver
 * is drawn as one — the picker does not OFFER them (`includeTerminated` is
 * off), but a form holding one still has to display them.
 */
export function employeeOption(employee: EmployeeResponse): TypeaheadOption {
  return {
    id: employee.id,
    label: employee.fullName,
    hint: employee.code,
    inactive: employee.status === 'terminated',
  };
}

/** The same row, in the reader's script — see `toOption` above. */
export function employeeOptionIn(ar: boolean): (employee: EmployeeResponse) => TypeaheadOption {
  return (employee) => ({
    id: employee.id,
    label: ar ? employee.fullNameAr : employee.fullName,
    hint: employee.code,
    inactive: employee.status === 'terminated',
  });
}
